import { useCallback, useState, useEffect } from 'react';
import { AModal, AInput, ASwitch, AInputSearch } from '../../../components';
import { IAddress } from '../../../shared/interfaces';
import { schemaAddress } from '../schemas/yup.address.schema';
import { useToast } from '../../../hooks/toast';
import { Col, Form, Row } from 'antd';
import axios from 'axios';
import { patchAddress, postAddress } from '../../../http';
import { useI18n } from '../../../shared/i18n';

interface IAddressProps {
  open: boolean;
  close: () => void;
  addressObj: IAddress | null;
  setAddresses: React.Dispatch<React.SetStateAction<IAddress[]>>;
  client_id: string | undefined;
}

const AddressCreateEdit: React.FC<IAddressProps> = ({
  open,
  close,
  addressObj,
  setAddresses,
  client_id,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const [cep, setCep] = useState<string>();
  const [address, setAddress] = useState<string>();
  const [number, setNumber] = useState<string>();
  const [complement, setComplement] = useState<string>();
  const [neighborhood, setNeighborhood] = useState<string>();
  const [city, setCity] = useState<string>();
  const [state, setState] = useState<string>();
  const [status, setStatus] = useState<boolean>(false);

  const searchCep = useCallback(async () => {
    await axios
      .get(`${process.env.REACT_APP_BRASIL_API}cep/v1/${cep}`)
      .then((res) => {
        setCep(res.data.cep);
        setAddress(res.data.street);
        setNeighborhood(res.data.neighborhood);
        setCity(res.data.city);
        setState(res.data.state);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: t('addressModal.toast.cepNotFoundTitle'),
          description: t('addressModal.toast.cepNotFoundDescription'),
        });
      });
  }, [addToast, cep, t]);

  const handleCreate = useCallback(async () => {
    schemaAddress
      .validate({
        client_id,
        cep,
        address,
        number,
        complement,
        neighborhood,
        city,
        state,
      })
      .then(async (validated) => {
        await postAddress(validated)
          .then((data) => {
            setAddresses((state) => [...state, data]);
            addToast({
              type: 'success',
              title: t('addressModal.toast.successTitle'),
              description: t('addressModal.toast.createdDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('addressModal.toast.errorTitle'),
              description: error.response.data.error,
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('addressModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [
    cep,
    address,
    number,
    complement,
    neighborhood,
    city,
    state,
    t,
    addToast,
    close,
    client_id,
    setAddresses,
  ]);

  const handleEdit = useCallback(async () => {
    schemaAddress
      .validate({
        cep,
        address,
        number,
        complement,
        neighborhood,
        city,
        state,
        status,
      })
      .then(async (validated) => {
        await patchAddress(addressObj?.id, validated)
          .then((data) => {
            setAddresses((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: t('addressModal.toast.infoTitle'),
              description: t('addressModal.toast.updatedDescription'),
            });
            close();
          })
          .catch((error) => {
            console.log(error.response.data);
            addToast({
              type: 'error',
              title: t('addressModal.toast.errorTitle'),
              description: error.response.data.error,
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('addressModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [
    cep,
    address,
    number,
    complement,
    neighborhood,
    city,
    state,
    status,
    t,
    addToast,
    close,
    addressObj,
    setAddresses,
  ]);

  useEffect(() => {
    if (addressObj) {
      setCep(addressObj.cep);
      setAddress(addressObj.address);
      setNumber(addressObj.number);
      setComplement(addressObj.complement);
      setNeighborhood(addressObj.neighborhood);
      setCity(addressObj.city);
      setState(addressObj.state);
      setStatus(addressObj.status);
    }
  }, [addressObj]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        addressObj ? t('addressModal.titleEdit') : t('addressModal.titleCreate')
      }
      onOk={addressObj ? handleEdit : handleCreate}
    >
      <Form layout="vertical">
        <Row>
          <Col span={12}>
            <Form.Item label={t('addressModal.fields.cep')} required>
              <AInputSearch
                placeholder={t('addressModal.fields.cepPlaceholder')}
                value={cep}
                onSearch={searchCep}
                maxLength={8}
                onChange={(e) => setCep(e.target.value)}
                enterButton
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('addressModal.fields.street')} required>
              <AInput
                placeholder={t('addressModal.fields.streetPlaceholder')}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item label={t('addressModal.fields.number')}>
              <AInput
                placeholder={t('addressModal.fields.numberPlaceholder')}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('addressModal.fields.neighborhood')} required>
              <AInput
                placeholder={t('addressModal.fields.neighborhoodPlaceholder')}
                value={neighborhood}
                onChange={(e) => setNeighborhood(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <Form.Item label={t('addressModal.fields.complement')}>
              <AInput
                placeholder={t('addressModal.fields.complementPlaceholder')}
                value={complement}
                onChange={(e) => setComplement(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={16}>
            <Form.Item label={t('addressModal.fields.city')} required>
              <AInput
                placeholder={t('addressModal.fields.cityPlaceholder')}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item label={t('addressModal.fields.state')} required>
              <AInput
                placeholder={t('addressModal.fields.statePlaceholder')}
                value={state}
                onChange={(e) => setState(e.target.value)}
                disabled
              />
            </Form.Item>
          </Col>
        </Row>

        {addressObj && (
          <Form.Item label={t('addressModal.fields.active')}>
            <ASwitch
              checked={status}
              checkedChildren={t('common.yes')}
              unCheckedChildren={t('common.no')}
              onChange={() => {
                setStatus(!status);
              }}
            />
          </Form.Item>
        )}
      </Form>
    </AModal>
  );
};

export default AddressCreateEdit;
